import { useEffect, useState } from "react";
import { api, type MailMessageDetail, type PublicMailAccount } from "../../lib/api";
import { RichMailEditor } from "./RichMailEditor";

export function ComposerModal({
  accounts,
  defaultAccountId,
  replyTo,
  onClose,
  onSent,
}: {
  accounts: PublicMailAccount[];
  defaultAccountId?: string;
  replyTo: MailMessageDetail | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [accountId, setAccountId] = useState(defaultAccountId || accounts[0]?.id || "");
  const [to, setTo] = useState(replyTo?.from?.match(/<([^>]+)>/)?.[1] || replyTo?.from || "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(
    replyTo ? (replyTo.subject.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`) : "",
  );
  const initialHtml = replyTo
    ? `<p></p><blockquote><p><em>Il ${replyTo.date ? new Date(replyTo.date).toLocaleString() : ""} ${replyTo.from} ha scritto:</em></p><p>${(replyTo.text || "").replace(/\n/g, "<br/>")}</p></blockquote>`
    : "<p></p>";
  const [html, setHtml] = useState(initialHtml);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [draftTick, setDraftTick] = useState(0);
  const [suggestions, setSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);

  useEffect(() => {
    const t = window.setInterval(() => setDraftTick((n) => n + 1), 15000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!draftTick) return;
    localStorage.setItem(
      "mailstorm.draft",
      JSON.stringify({ accountId, to, cc, bcc, subject, html, at: Date.now() }),
    );
  }, [draftTick, accountId, to, cc, bcc, subject, html]);

  useEffect(() => {
    const q = to.split(",").pop()?.trim() || "";
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void api.suggestContacts(q).then((r) => setSuggestions(r.contacts)).catch(() => setSuggestions([]));
    }, 180);
    return () => window.clearTimeout(handle);
  }, [to]);

  async function send() {
    if (!accountId) {
      setError("Scegli un account mittente");
      return;
    }
    setSending(true);
    setError("");
    try {
      const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      await api.send({
        accountId,
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        bcc: bcc
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        subject,
        html,
        text,
        inReplyTo: replyTo?.messageId || undefined,
      });
      localStorage.removeItem("mailstorm.draft");
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal modal-composer" onClick={(e) => e.stopPropagation()}>
        <div className="composer-chrome">
          <div>
            <div className="eyebrow">Compose</div>
            <strong className="composer-title">{replyTo ? "Rispondi" : "Nuovo messaggio"}</strong>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Chiudi">
            ✕
          </button>
        </div>
        <div className="composer">
          <div className="composer-fields">
            <div className="composer-field">
              <label className="label">Da</label>
              <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="composer-field">
              <label className="label">A</label>
              <input className="input" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" />
              {suggestions.length > 0 && (
                <div className="suggest-list">
                  {suggestions.map((s) => (
                    <button
                      key={s.email}
                      type="button"
                      className="suggest-item"
                      onClick={() => {
                        const parts = to.split(",");
                        parts[parts.length - 1] = ` ${s.email}`;
                        setTo(parts.join(",").replace(/^\s+/, ""));
                        setSuggestions([]);
                      }}
                    >
                      {s.displayName} · {s.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="composer-field-row">
              <div className="composer-field">
                <label className="label">Cc</label>
                <input className="input" value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setShowBcc((v) => !v)}>
                {showBcc ? "Nascondi Ccn" : "Ccn"}
              </button>
            </div>
            {showBcc && (
              <div className="composer-field">
                <label className="label">Ccn</label>
                <input className="input" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            )}
            <div className="composer-field">
              <label className="label">Oggetto</label>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          </div>

          <RichMailEditor initialHtml={initialHtml} onChange={setHtml} />

          <div className="composer-footer">
            <span className="composer-hint">Bozza locale ogni 15s · firma default al send</span>
            {error && <div className="error">{error}</div>}
            <div className="composer-actions">
              <button className="btn" type="button" onClick={onClose}>
                Annulla
              </button>
              <button className="btn btn-primary" onClick={() => void send()} disabled={sending}>
                {sending ? "Invio…" : "Invia"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
